// Resumen del gateway: cuánto se gastó este mes contra el tope, cuánto se usó y qué falló, y los primeros pasos
// cuando todavía no hay nada conectado.
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cargando, Input, Modal, PageHead, Tabla, TarjetaIndicador } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { diaCorto, numero, usd } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import { SoloSiAdministra } from '../componentes/Comunes';
import { ComoConectar } from '../componentes/ComoConectar';

const DIAS = [7, 30, 90];

// Una sola serie (llamadas por día): columnas finas de un solo color, la más alta con su número y el resto en la
// punta al pasar el cursor. La tabla de al lado dice lo mismo en cifras.
function Columnas({ dias, filas }: { dias: number; filas: { dia: string; llamadas: number; costo: number; fallidas: number }[] }) {
  const porDia = new Map(filas.map((f) => [f.dia, f]));
  const hoy = new Date();
  const serie = Array.from({ length: dias }, (_, i) => {
    const d = new Date(hoy.getTime() - (dias - 1 - i) * 86_400_000);
    const iso = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(d);
    return porDia.get(iso) ?? { dia: iso, llamadas: 0, costo: 0, fallidas: 0 };
  });
  const mayor = Math.max(0, ...serie.map((s) => s.llamadas));
  const max = Math.max(1, mayor);
  const iMax = mayor ? serie.findIndex((s) => s.llamadas === mayor) : -1;
  return (
    <div>
      <div className="columnas" role="img" aria-label={iMax < 0 ? `Sin llamadas en los últimos ${dias} días` : `Llamadas por día en los últimos ${dias} días; el día con más fue ${diaCorto(serie[iMax]!.dia)} con ${mayor}`}>
        {serie.map((s, i) => (
          <div key={s.dia} className="columna" tabIndex={0}>
            <i style={{ height: `${(s.llamadas / max) * 100}%` }} />
            {i === iMax && <span className="rotulo" style={{ bottom: `calc(${(s.llamadas / max) * 100}% + 2px)` }}>{numero(s.llamadas)}</span>}
            <span className="punta">{diaCorto(s.dia)} · {numero(s.llamadas)} llamadas{s.fallidas ? ` · ${numero(s.fallidas)} fallidas` : ''} · {usd(s.costo)}</span>
          </div>
        ))}
      </div>
      <div className="ejes"><span>{diaCorto(serie[0]!.dia)}</span><span>Hoy</span></div>
    </div>
  );
}

export function Resumen() {
  const { cuenta, aviso } = useSesion();
  const qc = useQueryClient();
  const [dias, setDias] = useState(30);
  const [tope, setTope] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const q = useQuery({ queryKey: ['resumen', dias], queryFn: () => api<any>(`/api/resumen?dias=${dias}`), refetchInterval: 60_000 });
  const guardarTope = useMutation({
    mutationFn: (v: string) => api('/api/ajustes', { presupuestoMensual: v === '' ? null : Number(v) }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['resumen'] }); aviso('Tope guardado'); setTope(null); },
    onError: (e) => setError(mensajeDeError(e)),
  });

  if (q.isLoading || !q.data) return <Cargando />;
  const { totales: t, conteos: c, porDia, porRecurso, porLlave } = q.data;
  const pasos = [
    { hecho: c.proveedores > 0, texto: 'Conectar un proveedor de modelos', a: '/modelos' },
    { hecho: c.modelos > 0, texto: 'Crear un modelo con el nombre que usarán los agentes', a: '/modelos' },
    { hecho: c.conectores > 0, texto: 'Conectar el POS, la contabilidad o el MCP de Glubi', a: '/conectores' },
    { hecho: c.llaves > 0, texto: 'Crear la llave del primer agente', a: '/llaves' },
  ];
  const pctTope = t.presupuesto_mes ? Math.round((t.gasto_mes / t.presupuesto_mes) * 100) : null;

  return (
    <div className="page">
      <PageHead title={`Gateway de ${cuenta?.cuenta}`} subtitle="Modelos, conectores y llaves del negocio en un solo lugar.">
        <div className="fila">{DIAS.map((d) => <button key={d} className={`chip ${d === dias ? 'tabla-row-selected' : ''}`} onClick={() => setDias(d)}>{d} días</button>)}</div>
      </PageHead>

      {pasos.some((p) => !p.hecho) && (
        <div className="card" style={{ marginBottom: 22 }}>
          <div className="card-head h-card">Primeros pasos</div>
          <div className="steps">
            {pasos.map((p, i) => (
              <Link key={i} to={p.a} className={`step ${p.hecho ? 'done' : pasos.findIndex((x) => !x.hecho) === i ? 'current' : ''}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                <span className="step-n">{p.hecho ? '✓' : i + 1}</span>
                <span style={{ fontSize: 14 }}>{p.texto}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="kpis">
        <TarjetaIndicador label="Gasto este mes" value={usd(t.gasto_mes)} tone={pctTope !== null && pctTope >= 90 ? 'error' : pctTope !== null && pctTope >= 70 ? 'pending' : 'neutral'}
          trend={t.presupuesto_mes !== null ? `${pctTope}% del tope de ${usd(t.presupuesto_mes)}` : 'Sin tope para el negocio'} />
        <TarjetaIndicador label="Llamadas" value={numero(t.llamadas)} trend={`${numero(t.herramientas)} a herramientas · ${dias} días`} />
        <TarjetaIndicador label="Fallidas" value={numero(t.errores + t.rechazadas)} tone={t.errores ? 'error' : 'neutral'} trend={`${numero(t.errores)} con error · ${numero(t.rechazadas)} rechazadas por permiso o tope`} />
        <TarjetaIndicador label="Tokens" value={numero(t.tokens_entrada + t.tokens_salida)} trend={`Mediana ${t.latencia_mediana === null ? '—' : `${numero(t.latencia_mediana)} ms`} por llamada`} />
      </div>

      <div className="dos-tercios">
        <div className="card" style={{ padding: '16px 20px' }}>
          <div className="entre" style={{ marginBottom: 6 }}>
            <span className="h-card">Llamadas por día</span>
            <span className="muted" style={{ fontSize: 12 }}>{usd(t.costo)} en {dias} días</span>
          </div>
          <Columnas dias={dias} filas={porDia} />
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div className="entre" style={{ marginBottom: 10 }}>
            <span className="h-card">Lo que está conectado</span>
            <SoloSiAdministra><Button size="sm" variant="ghost" icon="pencil" onClick={() => { setError(null); setTope(t.presupuesto_mes === null ? '' : String(t.presupuesto_mes)); }}>Tope del negocio</Button></SoloSiAdministra>
          </div>
          <div className="stack" style={{ gap: 8, fontSize: 14 }}>
            <Link to="/modelos">{numero(c.proveedores)} proveedores · {numero(c.modelos)} modelos</Link>
            <Link to="/conectores">{numero(c.conectores)} conectores{c.conectores_con_error ? ` · ${c.conectores_con_error} sin responder` : ''}</Link>
            <Link to="/llaves">{numero(c.llaves)} llaves activas</Link>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: 16, alignItems: 'start' }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Por modelo y herramienta</div>
          <Tabla rowKey="recurso" rows={porRecurso} vacio="Sin llamadas en este periodo."
            columns={[
              { key: 'recurso', label: 'Qué', render: (r: any) => <code>{r.recurso}</code> },
              { key: 'llamadas', label: 'Llamadas', numeric: true, render: (r: any) => numero(r.llamadas) },
              { key: 'fallidas', label: 'Fallidas', numeric: true, render: (r: any) => (r.fallidas ? numero(r.fallidas) : <span className="muted">—</span>) },
              { key: 'costo', label: 'Costo', numeric: true, render: (r: any) => usd(r.costo) },
            ]} />
        </div>
        <div>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Por llave</div>
          <Tabla rowKey="llave" rows={porLlave} vacio="Sin llamadas en este periodo."
            columns={[
              { key: 'llave', label: 'Llave' },
              { key: 'llamadas', label: 'Llamadas', numeric: true, render: (r: any) => numero(r.llamadas) },
              { key: 'costo', label: 'Costo', numeric: true, render: (r: any) => usd(r.costo) },
            ]} />
        </div>
      </div>

      <div className="card" style={{ marginTop: 22, padding: '18px 20px' }}>
        <div className="h-card" style={{ marginBottom: 6 }}>Cómo se conecta un agente</div>
        <ComoConectar />
      </div>

      {tope !== null && (
        <Modal title="Tope mensual del negocio" subtitle="Cuando el gasto del mes (hora de Bogotá) llega al tope, el gateway responde 429 a todas las llaves hasta el mes siguiente." onClose={() => setTope(null)}
          footer={<><Button variant="ghost" onClick={() => setTope(null)}>Cancelar</Button><Button disabled={guardarTope.isPending} onClick={() => guardarTope.mutate(tope)}>Guardar</Button></>}>
          <Input label="Tope (vacío: sin tope)" unit="US$ al mes" inputMode="decimal" value={tope} onChange={(e) => setTope(e.target.value.replace(',', '.'))} autoFocus />
          {error && <div className="error-caja">{error}</div>}
        </Modal>
      )}
    </div>
  );
}
